// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import OrderDetailsShipmentInfoDetails from "@electrowire/App/Components/OrderDetails/OrderDetailsShipmentInfoDetails";
import { orderLines, orderShipmentData, shipmentPackages } from "@gcg/Overrides/Pages/OrderDetailsPage";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import { Clickable, GridContainer, GridItem } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import parse from "html-react-parser";
import * as React from "react";
import { useContext, useState } from "react";
import { css } from "styled-components";

export interface OrderDetailsShipmentPackagesStyles {
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    shipmentDateCell?: DataTableCellProps;
    carrierCell?: DataTableCellProps;
    shipViaCell?: DataTableCellProps;
    trackingNumberCell?: DataTableCellProps;
    docLink?: LinkPresentationProps;
    deliveryLink?: ClickablePresentationProps;
    showAccordionGridStyle?: GridItemProps;
    deliveryLinkStyle?: LinkPresentationProps;
    shippingDetailsStyle?: LinkPresentationProps;
    accordionCellStyle?: DataTableCellProps;
}

export const shipmentPackagesStyles: OrderDetailsShipmentPackagesStyles = {
    docLink: {
        css: css`
            &,
            &:focus {
                outline: 0;
            }
        `,
        typographyProps: {
            css: css`
                font-size: 16px !important;
            `,
        },
    },
    deliveryLink: {
        css: css`
            color: ${getColor("primary.main")};

            &:hover,
            &:focus {
                text-decoration: underline;
            }

            &,
            &:focus {
                outline: 0;
            }
        `,
    },
};

const styles = shipmentPackagesStyles;

const OrderDetailsShipmentPackages: React.FC = () => {
    const [showAccordionById, setShowAccordionById] = useState({} as { [key: string]: boolean });
    const { value: order } = useContext(OrderStateContext);
    const constants = {
        docs: "DOCS",
        invoiceNumber: "INVOICE_NUMBER",
        deliveryNumber: "DELIVERY_NUMBER",
        lineNumber: "LINE_NUMBER",
        productNumber: "PRODUCT_NUMBER",
    };

    const displayShipmentPackageData = (id: any, task: any): any => {
        const shipmentPackageData = orderShipmentData[id];

        if (!shipmentPackageData) {
            return "-- --";
        }

        switch (task) {
            case constants.docs:
                // eslint-disable-next-line no-case-declarations
                const docs = shipmentPackageData.Attachments;

                if (!docs || (docs && docs.length === 0)) {
                    return "-- --";
                }

                return docs.map((doc: any, index: any) => {
                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Link key={`${id}${index}`} target="_blank" href={doc.FilePath} {...styles.docLink}>
                            {doc.FullName}
                        </Link>
                    );
                });

            case constants.deliveryNumber:
                if (!shipmentPackageData.DeliveryNumber) {
                    return "-- --";
                }

                return (
                    <Link
                        {...styles.deliveryLinkStyle}
                        icon={{
                            iconProps: {
                                src: showAccordionById[id] ? ChevronUp : ChevronDown,
                                size: 16,
                                color: "primary.main",
                                css: css`
                                    line-height: 0;
                                    margin-right: 5px;
                                    vertical-align: middle;
                                `,
                            },
                            position: "right",
                        }}
                        onClick={() => {
                            setShowAccordionById({
                                ...showAccordionById,
                                [id]: !showAccordionById[id],
                            });
                        }}
                    >
                        {shipmentPackageData.DeliveryNumber}
                    </Link>
                );
            case constants.invoiceNumber:
                if (!shipmentPackageData.InvoiceNumber) {
                    return "-- --";
                }

                return (
                    <Link href={`/MyAccount/Invoices/Details?invoiceNumber=${shipmentPackageData.InvoiceNumber}`}>
                        {shipmentPackageData.InvoiceNumber}
                    </Link>
                );
        }
    };

    if (!order) {
        return null;
    }

    if (!order.shipmentPackages || order.shipmentPackages.length === 0) {
        return null;
    }

    return (
        <DataTable {...styles.dataTable}>
            <DataTableHead {...styles.dataTableHead}>
                <DataTableHeader {...styles.shipmentDateHeader}>{translate("Shipment Date")}</DataTableHeader>
                <DataTableHeader {...styles.carrierHeader}>{translate("Carrier")}</DataTableHeader>
                <DataTableHeader {...styles.shipViaHeader}>{translate("Service")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Tracking #")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Packing Slip")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Invoice #")}</DataTableHeader>
            </DataTableHead>
            <DataTableBody {...styles.dataTableBody}>
                {order.shipmentPackages.map(shipmentPackage => (
                    <>
                        <DataTableRow {...styles.dataTableRow} key={shipmentPackage.id}>
                            <DataTableCell {...styles.shipmentDateCell}>
                                <LocalizedDateTime
                                    dateTime={new Date(shipmentPackage.shipmentDate)}
                                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                />
                            </DataTableCell>
                            <DataTableCell {...styles.carrierCell}>{shipmentPackage.carrier}</DataTableCell>
                            <DataTableCell {...styles.shipViaCell}>{shipmentPackage.shipVia}</DataTableCell>
                            <DataTableCell
                                {...styles.trackingNumberCell}
                                data-test-selector="orderDetails_TrackingNumber"
                            >
                                {shipmentPackage.trackingUrl.length > 0 ? (
                                    <Link href={shipmentPackage.trackingUrl}>{shipmentPackage.trackingNumber}</Link>
                                ) : (
                                    parse(shipmentPackage.trackingNumber, parserOptions)
                                )}
                            </DataTableCell>
                            <DataTableCell {...styles.trackingNumberCell}>
                                <GridItem {...styles.showAccordionGridStyle}>
                                    {shipmentPackages[shipmentPackage.id]?.shipmentPackageLineDtos?.length > 0 &&
                                        displayShipmentPackageData(shipmentPackage.id, constants.deliveryNumber)}
                                </GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.trackingNumberCell}>
                                {displayShipmentPackageData(shipmentPackage.id, constants.invoiceNumber)}
                            </DataTableCell>
                        </DataTableRow>
                        {showAccordionById[shipmentPackage.id] && (
                            <DataTableRow key={`shipping-package-${shipmentPackage.id}`} {...styles.dataTableRow}>
                                <DataTableCell colSpan={2} {...styles.accordionCellStyle}>
                                    <OrderDetailsShipmentInfoDetails
                                        orderLines={orderLines}
                                        orderLineShipments={
                                            shipmentPackages[shipmentPackage.id]?.shipmentPackageLineDtos
                                        }
                                    />
                                    <Link
                                        {...styles.shippingDetailsStyle}
                                        icon={{
                                            iconProps: {
                                                src: showAccordionById[shipmentPackage.id] ? ChevronUp : ChevronDown,
                                                size: 16,
                                                color: "primary.main",
                                                css: css`
                                                    line-height: 0;
                                                    margin-right: 5px;
                                                    vertical-align: middle;
                                                `,
                                            },
                                            position: "right",
                                        }}
                                        className="hide-on-desktop"
                                        onClick={() => {
                                            setShowAccordionById({
                                                ...showAccordionById,
                                                [shipmentPackage.id]: !showAccordionById[shipmentPackage.id],
                                            });
                                        }}
                                    >
                                        {orderShipmentData[shipmentPackage.id].DeliveryNumber}
                                    </Link>
                                </DataTableCell>
                            </DataTableRow>
                        )}
                    </>
                ))}
            </DataTableBody>
        </DataTable>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsShipmentPackages,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
